import React, { useState, useRef } from 'react';
import { Download, Printer, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { addWatermarkToPdf } from '../lib/pdfUtils';

interface TeacherAbsentyProps {
  onBack: () => void;
}

interface Teacher {
  id: number;
  masterListNo: string;
  name: string;
  school: string;
  joinDate: string;
  basicPay: string;
  currentJoinDate: string;
  totalWorkingDays: string;
  presentDays: string;
  absentDays: string;
  remarks: string;
}

export const TeacherAbsenty: React.FC<TeacherAbsentyProps> = ({ onBack }) => {
  const [selectedMonth, setSelectedMonth] = useState('मार्च 2026');
  const [teachers, setTeachers] = useState<Teacher[]>([
    { id: 1, masterListNo: '2', name: 'मनोज कुमार', school: 'उच्च माध्यमिक विद्यालय कटघरवाँ गोपालगंज', joinDate: '23-08-2013', basicPay: '35000', currentJoinDate: '25-07-2025', totalWorkingDays: '31', presentDays: '31', absentDays: '0', remarks: '' },
    { id: 2, masterListNo: '', name: 'पंकज कुमार दीपक', school: 'उच्च माध्यमिक विद्यालय कटघरवाँ गोपालगंज', joinDate: '16-11-2023', basicPay: '33950', currentJoinDate: '16-11-2023', totalWorkingDays: '31', presentDays: '31', absentDays: '0', remarks: '' },
    { id: 3, masterListNo: '', name: 'मनीषा त्रिपाठी', school: 'उच्च माध्यमिक विद्यालय कटघरवाँ गोपालगंज', joinDate: '16-11-2023', basicPay: '32890', currentJoinDate: '16-11-2023', totalWorkingDays: '31', presentDays: '31', absentDays: '0', remarks: '' },
    { id: 4, masterListNo: '', name: 'विशाल कुमार', school: 'उच्च माध्यमिक विद्यालय कटघरवाँ गोपालगंज', joinDate: '16-11-2023', basicPay: '32890', currentJoinDate: '16-11-2023', totalWorkingDays: '31', presentDays: '31', absentDays: '0', remarks: '' },
    { id: 5, masterListNo: '', name: 'सुरेन्द्र कुमार यादव', school: 'उच्च माध्यमिक विद्यालय कटघरवाँ गोपालगंज', joinDate: '09-02-2024', basicPay: '33950', currentJoinDate: '09-02-2024', totalWorkingDays: '31', presentDays: '31', absentDays: '0', remarks: '' },
    { id: 6, masterListNo: '', name: 'नवीन कुमार सिंह', school: 'उच्च माध्यमिक विद्यालय कटघरवाँ गोपालगंज', joinDate: '17-02-2024', basicPay: '33950', currentJoinDate: '23-06-2026', totalWorkingDays: '31', presentDays: '31', absentDays: '0', remarks: '' },
    { id: 7, masterListNo: '', name: 'अमिता कुमारी', school: 'उच्च माध्यमिक विद्यालय कटघरवाँ गोपालगंज', joinDate: '19-05-2025', basicPay: '32960', currentJoinDate: '19-05-2025', totalWorkingDays: '31', presentDays: '31', absentDays: '0', remarks: '' },
    { id: 8, masterListNo: '', name: 'अखिलेश कुमार', school: 'उच्च माध्यमिक विद्यालय कटघरवाँ गोपालगंज', joinDate: '20-05-2025', basicPay: '32960', currentJoinDate: '20-05-2025', totalWorkingDays: '31', presentDays: '31', absentDays: '0', remarks: '' },
  ]);

  const [globalWorkingDays, setGlobalWorkingDays] = useState('31');
  const [fontSize, setFontSize] = useState(10);
  const [cellPadding, setCellPadding] = useState(4);

  const months = [
    'जनवरी 2026', 'फरवरी 2026', 'मार्च 2026', 'अप्रैल 2026', 'मई 2026', 'जून 2026',
    'जुलाई 2026', 'अगस्त 2026', 'सितंबर 2026', 'अक्टूबर 2026', 'नवंबर 2026', 'दिसंबर 2026'
  ];

  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    if (!printRef.current) return;

    const element = printRef.current;
    const opt = {
      margin: [17, 10, 10, 10] as [number, number, number, number], // top, left, bottom, right in mm
      filename: `Teacher_Absenty_${selectedMonth.replace(/\s+/g, '_')}.pdf`,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { 
        scale: 2, 
        useCORS: true,
        letterRendering: true,
        scrollY: 0
      },
      jsPDF: { 
        unit: 'mm' as const, 
        format: 'a4' as const, 
        orientation: 'landscape' as const 
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    // New way to use html2pdf
    (html2pdf().set(opt).from(element).toPdf().get('pdf').then((pdf: any) => {
      addWatermarkToPdf(pdf);
    }) as any).save();
  };

  const updateTeacher = (id: number, field: keyof Teacher, value: string) => {
    setTeachers(teachers.map(t => {
      if (t.id === id) {
        const updated = { ...t, [field]: value };
        if (field === 'presentDays' || field === 'totalWorkingDays') {
          const working = parseInt(updated.totalWorkingDays) || 0;
          const present = parseInt(updated.presentDays) || 0;
          updated.absentDays = Math.max(0, working - present).toString();
        }
        return updated;
      }
      return t;
    }));
  };

  const addTeacher = () => {
    const newId = teachers.length > 0 ? Math.max(...teachers.map(t => t.id)) + 1 : 1;
    setTeachers([...teachers, {
      id: newId,
      masterListNo: '',
      name: '',
      school: teachers.length > 0 ? teachers[0].school : 'उच्च माध्यमिक विद्यालय कटघरवाँ गोपालगंज',
      joinDate: '',
      basicPay: '',
      currentJoinDate: '',
      totalWorkingDays: globalWorkingDays,
      presentDays: '',
      absentDays: globalWorkingDays,
      remarks: ''
    }]);
  };

  const removeTeacher = (id: number) => {
    setTeachers(teachers.filter(t => t.id !== id));
  };

  const updateGlobalWorkingDays = (days: string) => {
    setGlobalWorkingDays(days);
    setTeachers(teachers.map(t => {
      const working = parseInt(days) || 0;
      const present = parseInt(t.presentDays) || 0;
      return {
        ...t,
        totalWorkingDays: days,
        absentDays: Math.max(0, working - (isNaN(present) ? 0 : present)).toString()
      };
    }));
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 animate-fadeIn">
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          @page {
            size: A4;
            margin: 1.7cm 1cm 1cm 1cm;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print-container {
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}} />

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 print:hidden">
        <button 
          onClick={onBack}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-200 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="font-bold">Back to Dashboard</span>
        </button>
      </div>

      {/* Editor Section - Inspiring Layout */}
      <div className="bg-white p-4 md:p-8 rounded-2xl md:rounded-3xl shadow-xl border border-gray-100 mb-6 md:mb-10 print:hidden">
        <div className="flex flex-col items-center mb-6 md:mb-10">
          <div className="bg-emerald-50 px-4 md:px-6 py-2.5 md:py-3 rounded-xl md:rounded-2xl border border-emerald-100 flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
            <label className="text-lg md:text-xl font-bold text-emerald-800">MONTH —</label>
            <select 
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 border-2 border-emerald-200 rounded-xl focus:ring-4 focus:ring-emerald-100 outline-none font-bold bg-white text-emerald-900 cursor-pointer transition-all text-sm md:text-base"
            >
              {months.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <span className="text-emerald-600 font-semibold animate-pulse text-xs md:text-sm">→ Select Month</span>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-[40px_1fr_80px] md:grid-cols-[48px_1fr_120px] gap-3 md:gap-6 mb-4 md:mb-6 px-1 md:px-4">
            <div className="w-10 md:w-12"></div>
            <div className="text-left font-black text-gray-400 uppercase tracking-widest text-[10px] md:text-xs">TEACHER NAME</div>
            <div className="text-center font-black text-gray-400 uppercase tracking-widest text-[10px] md:text-xs">PRESENT</div>
          </div>

          <div className="space-y-3 md:space-y-4 mb-8 md:mb-10">
            {teachers.map((teacher, index) => (
              <div key={teacher.id} className="grid grid-cols-[40px_1fr_80px] md:grid-cols-[48px_1fr_120px] gap-3 md:gap-6 items-center group">
                <button 
                  onClick={() => removeTeacher(teacher.id)}
                  className="w-10 h-10 flex items-center justify-center bg-red-50 text-red-500 rounded-xl md:rounded-2xl hover:bg-red-500 hover:text-white transition-all shadow-sm group-hover:shadow-md"
                  title="Remove teacher"
                >
                  <Trash2 size={16} className="md:hidden" />
                  <Trash2 size={18} className="hidden md:block" />
                </button>
                <div className="relative flex items-center">
                  <span className="absolute -left-6 md:-left-8 font-black text-gray-300 text-sm md:text-lg">{index + 1}.</span>
                  <input 
                    type="text" 
                    value={teacher.name}
                    onChange={(e) => updateTeacher(teacher.id, 'name', e.target.value)}
                    className="w-full px-3 md:px-5 py-2.5 md:py-3 bg-gray-50 border-2 border-transparent rounded-xl md:rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none font-bold text-gray-800 transition-all placeholder:text-gray-300 text-sm md:text-base"
                    placeholder="Teacher Name..."
                  />
                </div>
                <input 
                  type="number" 
                  value={teacher.presentDays}
                  onChange={(e) => updateTeacher(teacher.id, 'presentDays', e.target.value)}
                  className="w-full px-2 md:px-4 py-2.5 md:py-3 bg-gray-50 border-2 border-transparent rounded-xl md:rounded-2xl focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none font-black text-emerald-600 text-center transition-all text-sm md:text-base"
                  placeholder="00"
                />
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center space-y-6 pt-6 border-t border-gray-100">
            <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-6 bg-gray-50 px-4 md:px-8 py-4 rounded-2xl md:rounded-3xl border border-gray-100 w-full sm:w-auto">
              <label className="font-black text-gray-500 uppercase tracking-wider text-[10px] md:text-sm text-center sm:text-left">Working Days for this Month:</label>
              <input 
                type="number" 
                value={globalWorkingDays}
                onChange={(e) => updateGlobalWorkingDays(e.target.value)}
                className="w-20 md:w-24 px-3 md:px-4 py-2 bg-white border-2 border-emerald-500 rounded-xl focus:ring-4 focus:ring-emerald-500/20 outline-none font-black text-emerald-600 text-center text-lg md:text-xl shadow-inner"
              />
            </div>

            {/* PDF Customization Controls */}
            <div className="w-full max-w-2xl bg-emerald-50/50 p-5 md:p-8 rounded-2xl md:rounded-[2rem] border border-emerald-100/50 shadow-inner">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h4 className="font-black text-emerald-900 uppercase tracking-tighter text-base md:text-lg">PDF Layout Settings</h4>
                <button 
                  onClick={() => { setFontSize(10); setCellPadding(4); }}
                  className="text-[10px] font-bold text-emerald-600 hover:text-emerald-800 underline uppercase tracking-widest"
                >
                  Reset
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
                <div className="space-y-3 md:space-y-4">
                  <div className="flex justify-between items-center">
                    <label className="font-bold text-emerald-800 text-xs md:text-sm uppercase tracking-wider flex items-center">
                      <span className="w-1.5 md:w-2 h-1.5 md:h-2 bg-emerald-500 rounded-full mr-2"></span>
                      Text Size
                    </label>
                    <span className="bg-emerald-600 text-white px-2 py-0.5 md:px-3 md:py-1 rounded-lg font-black text-[10px] md:text-xs shadow-sm">{fontSize}px</span>
                  </div>
                  <input 
                    type="range" 
                    min="6" 
                    max="20" 
                    value={fontSize}
                    onChange={(e) => setFontSize(parseInt(e.target.value))}
                    className="w-full h-1.5 md:h-2 bg-emerald-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                  />
                </div>

                <div className="space-y-3 md:space-y-4">
                  <div className="flex justify-between items-center">
                    <label className="font-bold text-emerald-800 text-xs md:text-sm uppercase tracking-wider flex items-center">
                      <span className="w-1.5 md:w-2 h-1.5 md:h-2 bg-emerald-500 rounded-full mr-2"></span>
                      Cell Padding
                    </label>
                    <span className="bg-emerald-600 text-white px-2 py-0.5 md:px-3 md:py-1 rounded-lg font-black text-[10px] md:text-xs shadow-sm">{cellPadding}px</span>
                  </div>
                  <input 
                    type="range" 
                    min="0" 
                    max="16" 
                    value={cellPadding}
                    onChange={(e) => setCellPadding(parseInt(e.target.value))}
                    className="w-full h-1.5 md:h-2 bg-emerald-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 w-full justify-center">
              <button 
                onClick={addTeacher}
                className="w-full sm:flex-1 sm:max-w-xs bg-emerald-600 hover:bg-emerald-700 text-white font-black py-3.5 md:py-4 rounded-xl md:rounded-2xl transition-all shadow-lg shadow-emerald-200 flex items-center justify-center space-x-2 md:space-x-3 transform active:scale-95"
              >
                <Plus size={20} className="md:hidden" strokeWidth={3} />
                <Plus size={24} className="hidden md:block" strokeWidth={3} />
                <span className="text-sm md:text-base">ADD NEW TEACHER</span>
              </button>
              
              <button 
                onClick={() => {
                  if (teachers.length > 0) {
                    removeTeacher(teachers[teachers.length - 1].id);
                  }
                }}
                className="w-full sm:flex-1 sm:max-w-xs bg-white border-2 border-red-200 hover:border-red-500 text-red-500 font-black py-3.5 md:py-4 rounded-xl md:rounded-2xl transition-all shadow-sm flex items-center justify-center space-x-2 md:space-x-3 transform active:scale-95"
              >
                <Trash2 size={20} className="md:hidden" />
                <Trash2 size={24} className="hidden md:block" />
                <span className="text-sm md:text-base">REMOVE LAST</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Section - Read Only & Polished */}
      <div className="bg-stone-50 p-6 md:p-12 rounded-[3rem] border-4 border-dashed border-stone-200 mb-12 print:hidden relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <Download size={200} />
        </div>
        
        <h3 className="text-center font-black text-stone-400 mb-10 uppercase tracking-[0.3em] text-sm">PREVIEW OF ABSENTY REPORT</h3>
        
        <div className="bg-white p-8 md:p-16 rounded-2xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.1)] border border-stone-200 overflow-x-auto mx-auto max-w-[1000px]">
          <div className="min-w-[900px]">
            <div className="text-center mb-8">
              <h1 className="text-xl md:text-2xl font-bold mb-2">प्रखण्ड का नाम - गोपालगंज</h1>
              <h2 className="text-lg md:text-xl font-bold mb-4">मास्टर सूची (माध्यमिक/उच्च माध्यमिक /प्रधानाध्यापक ,शिक्षक वर्ग 9-10 एवं 11-12 )</h2>
              <div className="flex justify-between font-bold text-base md:text-lg px-4">
                <span>विपत्र -1B</span>
                <span>माह का नाम:- {selectedMonth}</span>
              </div>
            </div>

            <table 
              className="w-full border-collapse border border-black text-center"
              style={{ fontSize: `${fontSize}px` }}
            >
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '40px' }}>क्रम संख्या</th>
                  <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '60px' }}>मास्टर सूची<br/>का क्रमांक</th>
                  <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '320px' }}>शिक्षक का नाम</th>
                  <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '80px' }}>विद्यालय का<br/>नाम</th>
                  <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '90px' }}>विद्यालय में प्रथम<br/>योगदान की तिथि</th>
                  <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '70px' }}>BASIC<br/>PAY</th>
                  <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '90px' }}>वर्तमान विद्यालय में<br/>योगदान तिथि</th>
                  <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '60px' }}>कुल कार्य<br/>दिवस</th>
                  <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '50px' }}>कुल उपस्थित<br/>दिन</th>
                  <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '40px' }}>कुल अनुपस्थित<br/>दिन</th>
                  <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '90px' }}>अभ्युक्ति</th>
                </tr>
              </thead>
              <tbody>
                {teachers.map((teacher, index) => (
                  <tr key={teacher.id}>
                    <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{index + 1}</td>
                    <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.masterListNo}</td>
                    <td className="border border-black font-bold" style={{ padding: `${cellPadding}px` }}>{teacher.name}</td>
                    {index === 0 && (
                      <td className="border border-black" style={{ padding: `${cellPadding}px` }} rowSpan={teachers.length}>
                        {teacher.school}
                      </td>
                    )}
                    <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.joinDate}</td>
                    <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.basicPay}</td>
                    <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.currentJoinDate}</td>
                    <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.totalWorkingDays}</td>
                    <td className="border border-black font-bold" style={{ padding: `${cellPadding}px` }}>{teacher.presentDays}</td>
                    <td className="border border-black font-bold" style={{ padding: `${cellPadding}px` }}>{teacher.absentDays}</td>
                    <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.remarks}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-16 flex justify-end pr-12 text-center font-bold">
              <div>
                <p>प्रधानाध्यापक</p>
                <p>उच्च माध्यमिक विद्यालय</p>
                <p>कटघरवाँ ,गोपालगंज</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-8">
          <button 
            onClick={handlePrint}
            className="flex items-center space-x-3 bg-emerald-600 hover:bg-emerald-700 text-white px-10 py-4 rounded-2xl font-bold shadow-lg transition-all transform hover:scale-105"
          >
            <Download size={24} />
            <span className="text-lg">DOWNLOAD PDF</span>
          </button>
        </div>
      </div>

      {/* Actual Print Content (Hidden on screen but used for PDF generation) */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, height: 0, overflow: 'hidden' }}>
        <div ref={printRef} className="bg-white text-black" style={{ width: '277mm', padding: '1mm' }}>
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">प्रखण्ड का नाम - गोपालगंज</h1>
            <h2 className="text-xl font-bold mb-4">मास्टर सूची (माध्यमिक/उच्च माध्यमिक /प्रधानाध्यापक ,शिक्षक वर्ग 9-10 एवं 11-12 )</h2>
            <div className="flex justify-between font-bold text-lg px-4">
              <span>विपत्र -1B</span>
              <span>माह का नाम:- {selectedMonth}</span>
            </div>
          </div>

          <table 
            className="w-full border-collapse border border-black text-center"
            style={{ fontSize: `${fontSize}px` }}
          >
            <thead>
              <tr>
                <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '40px' }}>क्रम संख्या</th>
                <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '60px' }}>मास्टर सूची<br/>का क्रमांक</th>
                <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '320px' }}>शिक्षक का नाम</th>
                <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '80px' }}>विद्यालय का<br/>नाम</th>
                <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '90px' }}>विद्यालय में प्रथम<br/>योगदान की तिथि</th>
                <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '70px' }}>BASIC<br/>PAY</th>
                <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '90px' }}>वर्तमान विद्यालय में<br/>योगदान तिथि</th>
                <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '60px' }}>कुल कार्य<br/>दिवस</th>
                <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '50px' }}>कुल उपस्थित<br/>दिन</th>
                <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '40px' }}>कुल अनुपस्थित<br/>दिन</th>
                <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '90px' }}>अभ्युक्ति</th>
              </tr>
            </thead>
            <tbody>
              {teachers.map((teacher, index) => (
                <tr key={teacher.id}>
                  <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{index + 1}</td>
                  <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.masterListNo}</td>
                  <td className="border border-black font-bold" style={{ padding: `${cellPadding}px` }}>{teacher.name}</td>
                  {index === 0 && (
                    <td className="border border-black" style={{ padding: `${cellPadding}px` }} rowSpan={teachers.length}>
                      {teacher.school}
                    </td>
                  )}
                  <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.joinDate}</td>
                  <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.basicPay}</td>
                  <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.currentJoinDate}</td>
                  <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.totalWorkingDays}</td>
                  <td className="border border-black font-bold" style={{ padding: `${cellPadding}px` }}>{teacher.presentDays}</td>
                  <td className="border border-black font-bold" style={{ padding: `${cellPadding}px` }}>{teacher.absentDays}</td>
                  <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.remarks}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-16 flex justify-end pr-12 text-center font-bold text-sm">
            <div>
              <p>प्रधानाध्यापक</p>
              <p>उच्च माध्यमिक विद्यालय</p>
              <p>कटघरवाँ ,गोपालगंज</p>
            </div>
          </div>
        </div>
      </div>

      {/* Legacy Print Content (Still useful for browser Ctrl+P) */}
      <div className="hidden print:block print-container">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-2">प्रखण्ड का नाम - गोपालगंज</h1>
          <h2 className="text-xl font-bold mb-4">मास्टर सूची (माध्यमिक/उच्च माध्यमिक /प्रधानाध्यापक ,शिक्षक वर्ग 9-10 एवं 11-12 )</h2>
          <div className="flex justify-between font-bold text-lg px-4">
            <span>विपत्र -1B</span>
            <span>माह का नाम:- {selectedMonth}</span>
          </div>
        </div>

        <table 
          className="w-full border-collapse border border-black text-center"
          style={{ fontSize: `${fontSize}px` }}
        >
          <thead>
            <tr>
              <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '40px' }}>क्रम संख्या</th>
              <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '60px' }}>मास्टर सूची<br/>का क्रमांक</th>
              <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '320px' }}>शिक्षक का नाम</th>
              <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '80px' }}>विद्यालय का<br/>नाम</th>
              <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '90px' }}>विद्यालय में प्रथम<br/>योगदान की तिथि</th>
              <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '70px' }}>BASIC<br/>PAY</th>
              <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '90px' }}>वर्तमान विद्यालय में<br/>योगदान तिथि</th>
              <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '60px' }}>कुल कार्य<br/>दिवस</th>
              <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '50px' }}>कुल उपस्थित<br/>दिन</th>
              <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '40px' }}>कुल अनुपस्थित<br/>दिन</th>
              <th className="border border-black font-bold" style={{ padding: `${cellPadding}px`, width: '90px' }}>अभ्युक्ति</th>
            </tr>
          </thead>
          <tbody>
            {teachers.map((teacher, index) => (
              <tr key={teacher.id}>
                <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{index + 1}</td>
                <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.masterListNo}</td>
                <td className="border border-black font-bold" style={{ padding: `${cellPadding}px` }}>{teacher.name}</td>
                {index === 0 && (
                  <td className="border border-black" style={{ padding: `${cellPadding}px` }} rowSpan={teachers.length}>
                    {teacher.school}
                  </td>
                )}
                <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.joinDate}</td>
                <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.basicPay}</td>
                <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.currentJoinDate}</td>
                <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.totalWorkingDays}</td>
                <td className="border border-black font-bold" style={{ padding: `${cellPadding}px` }}>{teacher.presentDays}</td>
                <td className="border border-black font-bold" style={{ padding: `${cellPadding}px` }}>{teacher.absentDays}</td>
                <td className="border border-black" style={{ padding: `${cellPadding}px` }}>{teacher.remarks}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-24 flex justify-end pr-12 text-center font-bold text-lg">
          <div>
            <p>प्रधानाध्यापक</p>
            <p>उच्च माध्यमिक विद्यालय</p>
            <p>कटघरवाँ ,गोपालगंज</p>
          </div>
        </div>
      </div>
    </div>
  );
};
